import { supabase } from "../config/supabaseClient";
import { debug } from "../Utils/debug";
import {
  createSuccessResponse,
  createErrorResponse,
  logger,
} from "../Utils/ErrorHandler";
import { executeQuery } from "./utils/supabaseQueryWrapper";

const NHTSA_API_BASE = "https://vpic.nhtsa.dot.gov/api";

// Cache getClientId result for 30 seconds to avoid redundant auth+DB queries
// when multiple vehicle operations fire in quick succession
let _clientIdCache = { value: null, expiry: 0 };

export const clientVinService = {
  decodeVin: async (vin) => {
    try {
      if (!clientVinService.validateVinFormat(vin)) {
        return createErrorResponse(
          "Invalid VIN format. VIN must be 17 characters."
        );
      }

      const response = await fetch(
        `${NHTSA_API_BASE}/vehicles/DecodeVinValues/${vin}?format=json`
      );
      if (!response.ok) throw new Error("Failed to decode VIN");

      const data = await response.json();

      if (!data.Results || data.Results.length === 0) {
        return createErrorResponse("No vehicle data found for this VIN");
      }

      const result = data.Results[0];

      if (result.ErrorCode !== "0") {
        return createErrorResponse(result.ErrorText || "Unable to decode VIN");
      }

      return createSuccessResponse({
        vin: vin.toUpperCase(),
        year: result.ModelYear || "Unknown",
        make: result.Make || "Unknown",
        model: result.Model || "Unknown",
        trim: result.Trim || "",
        bodyClass: result.BodyClass || "Unknown",
        engineSize:
          result.DisplacementL || result.DisplacementCC || "Unknown",
        cylinders: result.EngineCylinders || "Unknown",
        fuelType: result.FuelTypePrimary || "Unknown",
        transmission: result.TransmissionStyle || "Unknown",
        driveType: result.DriveType || "Unknown",
        vehicleType: result.VehicleType || "Unknown",
        manufacturer: result.Manufacturer || "Unknown",
        plantCity: result.PlantCity || "",
        plantCountry: result.PlantCountry || "",
      });
    } catch (error) {
      return createErrorResponse(
        "Network error. Please check your connection and try again."
      );
    }
  },

  validateVinFormat: (vin) => {
    if (!vin || typeof vin !== "string") return false;
    if (vin.length !== 17) return false;
    return /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
  },

  getClientId: async () => {
    // Return cached result if still valid (avoids redundant auth+DB queries)
    if (_clientIdCache.value && Date.now() < _clientIdCache.expiry) {
      return _clientIdCache.value;
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        // Only log if there's an actual error, not just no user
        if (userError) {
          logger.error("User authentication error:", userError);
        }
        return createErrorResponse("User not authenticated");
      }

      debug.api("Authenticated user:", user.id);

      // Use maybeSingle() instead of single() to avoid 406 errors when no row found
      const { data: client, error: clientError } = await supabase
        .from("clients")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      // Handle database errors (not "no rows found" which is handled by maybeSingle returning null)
      if (clientError) {
        logger.error("Client lookup error:", clientError);
        return createErrorResponse(`Database error: ${clientError.message}`);
      }

      // Handle case where client profile doesn't exist
      if (!client) {
        // Return gracefully without throwing - this is expected for new users or garage/admin users
        return createErrorResponse(
          "Client profile not found. Please create your profile first by going to your profile settings."
        );
      }

      debug.api("Found client:", client.id);
      const result = { success: true, clientId: client.id };
      // Cache for 30 seconds
      _clientIdCache = { value: result, expiry: Date.now() + 30000 };
      return result;
    } catch (error) {
      logger.error("getClientId error:", error);
      return createErrorResponse(error.message);
    }
  },

  saveClientVehicle: async (vehicleData) => {
    try {
      debug.api("Starting vehicle save process...");

      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        throw new Error(clientResult.error);
      }

      debug.api("Client ID obtained:", clientResult.clientId);

      // Parse year as integer - handle "Unknown" or invalid values
      const parsedYear = parseInt(vehicleData.year, 10);
      const validYear = !isNaN(parsedYear) && parsedYear > 1900 && parsedYear <= new Date().getFullYear() + 1
        ? parsedYear
        : null;

      // Parse cylinders as integer - handle "Unknown" or invalid values
      const parsedCylinders = parseInt(vehicleData.cylinders, 10);
      const validCylinders = !isNaN(parsedCylinders) && parsedCylinders > 0 && parsedCylinders <= 16
        ? parsedCylinders
        : null;

      const vehicleToSave = {
        vin: vehicleData.vin,
        year: validYear,
        make: vehicleData.make,
        model: vehicleData.model,
        body_class:
          vehicleData.bodyClass || vehicleData.bodyType || "Not specified",
        engine_size:
          vehicleData.engineSize || vehicleData.engine || "Not specified",
        fuel_type: vehicleData.fuelType || "Not specified",
        cylinders: validCylinders,
        nickname:
          vehicleData.nickname ||
          `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`,
        notes: vehicleData.notes || "",
        current_mileage: vehicleData.currentMileage || 0,
        mileage_unit: vehicleData.mileageUnit || "km",
        mileage_km: vehicleData.mileageKm || vehicleData.currentMileage || 0,
        mileage_miles: vehicleData.mileageMiles || 0,
        manual_entry: vehicleData.manualEntry || false,
        client_id: clientResult.clientId,
        garage_id: null, // Client vehicles are independent
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      debug.api("Vehicle data to save:", vehicleToSave);

      const { data, error } = await supabase
        .from("vehicles")
        .insert([vehicleToSave])
        .select()
        .single();

      if (error) {
        logger.error("Supabase insert error:", error);
        throw new Error(`Database error: ${error.message}`);
      }

      debug.api("Vehicle saved successfully:", data);

      const existingVehicles = JSON.parse(
        sessionStorage.getItem("client_vehicles") || "[]"
      );
      existingVehicles.push({ ...data, id: data.id });
      sessionStorage.setItem(
        "client_vehicles",
        JSON.stringify(existingVehicles)
      );

      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error saving client vehicle:", error);
      return createErrorResponse(error.message);
    }
  },

  loadClientVehicles: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return createSuccessResponse([]);
      }

      const result = await executeQuery(
        supabase
          .from("vehicles")
          .select("*")
          .eq("client_id", clientResult.clientId)
          .neq("archived", true)
          .order("created_at", { ascending: false }),
        { context: "Loading client vehicles", defaultValue: [] }
      );

      if (result.success) {
        sessionStorage.setItem("client_vehicles", JSON.stringify(result.data));
      }

      return result;
    } catch (error) {
      logger.error("Error loading client vehicles:", error);
      return createErrorResponse(error.message);
    }
  },

  loadArchivedVehicles: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return createSuccessResponse([]);
      }

      return executeQuery(
        supabase
          .from("vehicles")
          .select("*")
          .eq("client_id", clientResult.clientId)
          .eq("archived", true)
          .order("archived_at", { ascending: false }),
        { context: "Loading archived vehicles", defaultValue: [] }
      );
    } catch (error) {
      logger.error("Error loading archived vehicles:", error);
      return createErrorResponse(error.message);
    }
  },

  archiveClientVehicle: async (vehicleId, archiveReason = null) => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        throw new Error(clientResult.error);
      }

      const updates = {
        archived: true,
        archived_at: new Date().toISOString(),
        archive_reason: archiveReason || null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("vehicles")
        .update(updates)
        .eq("id", vehicleId)
        .eq("client_id", clientResult.clientId)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error("Vehicle not found or update failed");
      }

      // Update sessionStorage cache - remove archived vehicle
      const existingVehicles = JSON.parse(
        sessionStorage.getItem("client_vehicles") || "[]"
      );
      const updatedVehicles = existingVehicles.filter(
        (vehicle) => vehicle.id !== vehicleId
      );
      sessionStorage.setItem("client_vehicles", JSON.stringify(updatedVehicles));

      return createSuccessResponse(data[0]);
    } catch (error) {
      logger.error("Error archiving vehicle:", error);
      return createErrorResponse(error.message);
    }
  },

  unarchiveClientVehicle: async (vehicleId) => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        throw new Error(clientResult.error);
      }

      const updates = {
        archived: false,
        archived_at: null,
        archive_reason: null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("vehicles")
        .update(updates)
        .eq("id", vehicleId)
        .eq("client_id", clientResult.clientId)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error("Vehicle not found or update failed");
      }

      // Update sessionStorage cache - add unarchived vehicle back
      const existingVehicles = JSON.parse(
        sessionStorage.getItem("client_vehicles") || "[]"
      );
      existingVehicles.unshift(data[0]);
      sessionStorage.setItem("client_vehicles", JSON.stringify(existingVehicles));

      return createSuccessResponse(data[0]);
    } catch (error) {
      logger.error("Error unarchiving vehicle:", error);
      return createErrorResponse(error.message);
    }
  },

  updateClientVehicle: async (vehicleId, updates) => {
    try {
      debug.api("Starting vehicle update process...");

      // Get the current client ID for security validation
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        throw new Error(clientResult.error);
      }

      debug.api("Client ID obtained:", clientResult.clientId);
      debug.api("Updating vehicle:", vehicleId, "with data:", updates);

      // Update the vehicle in Supabase with security check
      const { data, error } = await supabase
        .from("vehicles")
        .update(updates)
        .eq("id", vehicleId)
        .eq("client_id", clientResult.clientId) // Security: only update if vehicle belongs to this client
        .select()
        .single();

      if (error) {
        logger.error("Supabase update error:", error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data) {
        throw new Error(
          "Vehicle not found or you don't have permission to update it"
        );
      }

      debug.api("Vehicle updated successfully:", data);

      // Update sessionStorage cache
      const existingVehicles = JSON.parse(
        sessionStorage.getItem("client_vehicles") || "[]"
      );

      const updatedVehicles = existingVehicles.map((vehicle) =>
        vehicle.id === vehicleId ? { ...vehicle, ...data } : vehicle
      );

      sessionStorage.setItem(
        "client_vehicles",
        JSON.stringify(updatedVehicles)
      );

      return createSuccessResponse(data);
    } catch (error) {
      logger.error("Error updating client vehicle:", error);
      return createErrorResponse(error.message);
    }
  },

  getSavedVehicles: () => {
    try {
      return JSON.parse(sessionStorage.getItem("client_vehicles") || "[]");
    } catch (error) {
      return [];
    }
  },

  saveVehicle: async (vehicleData) => {
    return await clientVinService.saveClientVehicle(vehicleData);
  },
};
